<template>
  <div class="flow-step" :class="stepClasses">
    <div class="flow-marker"></div>

    <div class="flow-content">
      <div class="flow-header">
        <div class="flex align-items-center gap-2">
          <h4 class="flow-title">{{ step.title }}</h4>
          <a-tag class="m-0" :color="step.statusColor">{{ step.statusTitle }}</a-tag>
        </div>
        <span v-if="step.date" class="flow-date">{{ step.date }}</span>
      </div>

      <p v-if="step.description" class="flow-description">{{ step.description }}</p>

      <div v-if="showProgress" class="flow-progress">
        <div class="flow-progress-track">
          <div class="flow-progress-bar" :style="{ width: `${progressPercent}%` }"></div>
        </div>
        <div class="flow-progress-meta">
          <span>{{ completedDocumentsCount }} of {{ step.documents.length }} required completed</span>
          <span>{{ progressPercent }}%</span>
        </div>
      </div>

      <button v-if="step.documents.length" type="button" class="flow-toggle" @click="expanded = !expanded">
        <a-icon :type="expanded ? 'down' : 'right'" />
        {{ documentsLabel }}
      </button>

      <template v-if="expanded && step.documents.length">
        <div class="flow-documents">
          <div v-for="document in visibleDocuments" :key="document.key" class="flow-document">
            <button
              type="button"
              class="flow-document-open"
              :class="{ 'flow-document--disabled': !document.canOpen }"
              @click="openDocument(document)"
            >
              <div class="flow-document-info">
                <span class="flow-document-name">{{ document.name }}</span>
                <span class="flow-document-meta">{{ document.meta }}</span>
              </div>
              <div class="flow-document-side">
                <a-tag v-if="document.status" class="m-0" :color="document.statusColor">{{ document.status }}</a-tag>
                <a-icon v-if="document.canOpen" type="eye" />
              </div>
            </button>
            <button-more
              v-if="document.canArchive"
              :record="document"
              :menu-items="documentMenuItems"
              trigger-test-id="profile-training-actions-button"
              menu-test-id="profile-training-actions-menu"
            >
              <template #trigger>
                <button
                  type="button"
                  class="flow-document-actions"
                  :aria-label="`Actions for ${document.name}`"
                  data-testid="profile-training-actions-button"
                  @click.stop
                >
                  <a-icon type="ellipsis" />
                </button>
              </template>
            </button-more>
          </div>
        </div>

        <button v-if="canShowMore" type="button" class="flow-show-more" @click="showMoreDocuments">
          Show more {{ hiddenDocumentsCount }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { Icon, Tag } from 'ant-design-vue'
import ButtonMore from '@/components/ui/button-more.vue'

export default {
  name: 'ProfileCompanyFlowStep',
  components: {
    'a-icon': Icon,
    'a-tag': Tag,
    'button-more': ButtonMore
  },
  props: {
    step: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      expanded: false,
      documentsLimit: 20,
      documentsPerPage: 20
    }
  },
  computed: {
    documentMenuItems() {
      return [
        {
          key: 'archive',
          icon: 'carbon:archive',
          label: 'Archive',
          visible: true,
          testId: 'profile-training-archive-action',
          handler: document => this.$emit('archive-document', document)
        }
      ]
    },

    visibleDocuments() {
      return this.step.documents.slice(0, this.documentsLimit)
    },

    canShowMore() {
      return this.visibleDocuments.length < this.step.documents.length
    },

    hiddenDocumentsCount() {
      return this.step.documents.length - this.visibleDocuments.length
    },

    showProgress() {
      return this.step.current && this.step.documents.length > 0
    },

    completedDocumentsCount() {
      return this.step.documents.filter(document => document.completed).length
    },

    progressPercent() {
      if (!this.step.documents.length) return 0

      return Math.round((this.completedDocumentsCount / this.step.documents.length) * 100)
    },

    documentsLabel() {
      const count = this.step.documents.length
      const noun = count === 1 ? 'document' : 'documents'

      if (this.step.key === 'invite' && this.step.completed) {
        return `${count} ${noun} signed`
      }

      return `${count} ${noun}`
    },

    stepClasses() {
      return {
        'flow-step--completed': this.step.completed,
        'flow-step--current': this.step.current,
        'flow-step--danger': this.step.danger,
        'flow-step--muted': this.step.muted
      }
    }
  },
  watch: {
    expanded(newVal) {
      if (!newVal) {
        this.documentsLimit = this.documentsPerPage
      }
    },

    'step.documents.length'() {
      this.documentsLimit = this.documentsPerPage
    }
  },
  methods: {
    showMoreDocuments() {
      this.documentsLimit += this.documentsPerPage
    },

    openDocument(document) {
      if (!document.canOpen) return

      this.$emit('open-document', document)
    }
  }
}
</script>

<style scoped>
.flow-step {
  position: relative;
  display: flex;
  gap: 20px;
  color: var(--text-color);
}

.flow-step::before {
  content: '';
  position: absolute;
  left: 7px;
  top: 20px;
  bottom: -22px;
  width: 1px;
  background: var(--border-color-base);
}

.flow-step:last-child::before {
  display: none;
}

.flow-marker {
  position: relative;
  z-index: 1;
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  margin-top: 4px;
  border-radius: 50%;
  background: var(--border-color-base);
  border: 2px solid var(--bg-component);
}

.flow-step--completed .flow-marker {
  background: #10b981;
}

.flow-step--current .flow-marker {
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.25);
}

.flow-step--danger .flow-marker {
  background: #ef4444;
}

.flow-step--muted {
  opacity: 0.55;
}

.flow-content {
  width: 100%;
  padding-bottom: 22px;
}

.flow-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.flow-title {
  margin: 0;
  font-size: 15px;
}

.flow-date,
.flow-description,
.flow-document-meta {
  color: var(--text-color-secondary);
}

.flow-description {
  margin: 4px 0 8px;
}

.flow-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 8px 0 10px;
}

.flow-progress-track {
  height: 3px;
  overflow: hidden;
  background: var(--border-color-base);
  border-radius: 999px;
}

.flow-progress-bar {
  height: 100%;
  background: var(--primary-color);
  border-radius: 999px;
  transition: width 0.2s ease;
}

.flow-progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 600;
}

.flow-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 14px;
  color: var(--text-color);
  background: var(--bg-component);
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.flow-documents {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}

.flow-document {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  color: var(--text-color);
  background: var(--bg-component);
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
}

.flow-document-open {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 0;
  text-align: left;
  color: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.flow-document-actions {
  flex-shrink: 0;
  padding: 4px 8px;
  color: var(--text-color);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.flow-document--disabled {
  cursor: default;
}

.flow-document-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.flow-document-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-document-side {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.flow-show-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  margin-top: 8px;
  padding: 0 12px;
  color: var(--primary-color);
  background: transparent;
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

@media screen and (max-width: 640px) {
  .flow-header,
  .flow-document-open {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
